/**
 * InfoPanel Component
 *
 * Bottom panel with tabs: Problems, Output, Terminal, Git.
 */
import { useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Terminal as TerminalIcon,
  GitBranch,
  FileOutput,
  X,
  ChevronDown,
  Trash2,
  Play,
} from 'lucide-react';
import { useBuildStore } from '@/stores/buildStore';
import { useUIStore } from '@/stores/uiStore';
import clsx from 'clsx';

type TabType = 'problems' | 'output' | 'terminal' | 'git';

// E.21.30 (2026-05-04): Problems are now sourced from the live buildStore
// instead of a hardcoded mockProblems array. Errors + warnings populate when
// pollBuildStatus reaches a terminal state and parseLogsForProblems extracts
// structured BuildError[] from the backend's `result.error` text (or
// synthesizes a single entry from raw npm/Docker error output when no
// regex matches — see buildStore.ts:parseLogsForProblems N7 fallback).

function ProblemsTab() {
  const errors = useBuildStore((s) => s.buildStatus.errors) ?? [];
  const warnings = useBuildStore((s) => s.buildStatus.warnings) ?? [];
  const healed = useBuildStore((s) => s.buildStatus.healed);
  const allProblems = [...errors, ...warnings];

  return (
    <div className="h-full flex flex-col">
      {/* Summary */}
      <div className="flex items-center gap-4 px-3 py-2 border-b border-vibe-border text-sm">
        <span className="flex items-center gap-1 text-vibe-error">
          <AlertCircle className="w-4 h-4" />
          {errors.length} Errors
        </span>
        <span className="flex items-center gap-1 text-vibe-warning">
          <AlertTriangle className="w-4 h-4" />
          {warnings.length} Warnings
        </span>
      </div>

      {/* E.self-heal — the build pipeline auto-recovered a corrupt/missing
          native dependency this build. Calm one-line notice so the heal isn't
          buried in the raw logs. Only shown when errors are absent (a healed
          build that still failed shows the error, not this). */}
      {healed && errors.length === 0 && (
        <div
          className="flex items-center gap-2 px-3 py-2 border-b border-vibe-border text-sm text-vibe-success"
          data-testid="heal-notice"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            Recovered a corrupt build dependency automatically — reinstalled and
            rebuilt.
          </span>
        </div>
      )}

      {/* Problems List */}
      <div className="flex-1 overflow-auto" data-testid="problems-list">
        {allProblems.length === 0 ? (
          <div className="flex items-center justify-center h-full text-vibe-text-secondary text-sm">
            No problems detected
          </div>
        ) : (
          <div className="py-1">
            {allProblems.map((problem, idx) => (
              <div
                key={`${problem.file}:${problem.line}:${problem.column}:${idx}`}
                className="flex items-start gap-2 px-3 py-1.5 hover:bg-vibe-bg-tertiary cursor-pointer text-sm"
              >
                {problem.severity === 'error' ? (
                  <AlertCircle className="w-4 h-4 text-vibe-error flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-vibe-warning flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-vibe-text truncate">{problem.message}</div>
                  <div className="text-vibe-text-secondary text-xs">
                    {problem.file}:{problem.line}:{problem.column}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OutputTab() {
  // E.21.31.2 (2026-05-04): rendered live from buildStore.buildLogs which
  // pollBuildStatus populates each tick via GET /api/builds/{id}/logs.
  // Pre-fix this rendered hardcoded fake "Vite v5.0.0 ready in 342 ms"
  // text — the user couldn't see actual build progress or failure
  // diagnostics.
  const buildLogs = useBuildStore((s) => s.buildLogs);
  const clearBuildLogs = useBuildStore((s) => s.clearBuildLogs);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-vibe-border">
        <span className="text-xs text-vibe-text-secondary px-2 py-1">
          Build Output
        </span>
        <button
          className="p-1 hover:bg-vibe-bg-tertiary rounded text-vibe-text-secondary"
          onClick={clearBuildLogs}
          title="Clear logs"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Output content */}
      <div className="flex-1 overflow-auto font-mono text-sm p-3" data-testid="output-log">
        {buildLogs.length > 0 ? (
          buildLogs.map((log, index) => (
            <div key={index} className="text-vibe-text-secondary whitespace-pre-wrap break-all">
              {log}
            </div>
          ))
        ) : (
          <div className="text-vibe-text-secondary italic">
            No build output yet. Click Build to start.
          </div>
        )}
      </div>
    </div>
  );
}

function TerminalTab() {
  const [history, setHistory] = useState<string[]>([
    '$ npm run dev',
    '',
    '> vibe-designer@1.0.0 dev',
    '> vite',
    '',
    'Server running at http://localhost:5173',
  ]);
  const [input, setInput] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      setHistory([...history, `$ ${input}`, 'Command executed.']);
      setInput('');
    }
  };

  return (
    <div className="h-full flex flex-col font-mono text-sm">
      {/* Terminal content */}
      <div className="flex-1 overflow-auto p-3">
        {history.map((line, index) => (
          <div
            key={index}
            className={clsx(
              'whitespace-pre',
              line.startsWith('$') ? 'text-vibe-accent' : 'text-vibe-text-secondary'
            )}
          >
            {line || '\u00A0'}
          </div>
        ))}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex items-center border-t border-vibe-border">
        <span className="px-3 text-vibe-accent">$</span>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="flex-1 bg-transparent text-vibe-text py-2 pr-3 outline-none"
          placeholder="Enter command..."
        />
      </form>
    </div>
  );
}

function GitTab() {
  const [changes] = useState([
    { file: 'src/App.tsx', status: 'modified' },
    { file: 'src/components/Header.tsx', status: 'added' },
    { file: 'src/styles/main.css', status: 'modified' },
  ]);

  return (
    <div className="h-full flex flex-col">
      {/* Branch info */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-vibe-border">
        <div className="flex items-center gap-2 text-sm">
          <GitBranch className="w-4 h-4 text-vibe-accent" />
          <span className="text-vibe-text">main</span>
        </div>
        <div className="flex items-center gap-1">
          <button className="p-1 hover:bg-vibe-bg-tertiary rounded text-vibe-text-secondary">
            <Play className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Changes */}
      <div className="flex-1 overflow-auto">
        <div className="px-3 py-2">
          <div className="text-xs text-vibe-text-secondary uppercase mb-2">
            Changes ({changes.length})
          </div>
          {changes.map((change, index) => (
            <div
              key={index}
              className="flex items-center gap-2 py-1 text-sm hover:bg-vibe-bg-tertiary rounded px-2 -mx-2 cursor-pointer"
            >
              <span
                className={clsx(
                  'w-1.5 h-1.5 rounded-full',
                  change.status === 'added' && 'bg-vibe-success',
                  change.status === 'modified' && 'bg-vibe-warning',
                  change.status === 'deleted' && 'bg-vibe-error'
                )}
              />
              <span className="text-vibe-text truncate">{change.file}</span>
              <span className="text-xs text-vibe-text-secondary ml-auto">
                {change.status.charAt(0).toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function InfoPanel() {
  const [activeTab, setActiveTab] = useState<TabType>('problems');
  // 2026-07-11 — close is driven through uiStore.infoCollapsed (AppShell owns
  // the resizable Panel and collapses/expands it via a ref). The preview
  // toolbar's "Show panel" button reopens it. Replaces the old local-only
  // "minimize" that left the panel's reserved height as an empty gap.
  const setInfoCollapsed = useUIStore((s) => s.setInfoCollapsed);

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'problems', label: 'Problems', icon: <AlertCircle className="w-4 h-4" /> },
    { id: 'output', label: 'Output', icon: <FileOutput className="w-4 h-4" /> },
    { id: 'terminal', label: 'Terminal', icon: <TerminalIcon className="w-4 h-4" /> },
    { id: 'git', label: 'Git', icon: <GitBranch className="w-4 h-4" /> },
  ];

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Tab bar */}
      <div className="flex items-center justify-between border-b border-vibe-border bg-vibe-bg-secondary">
        <div className="flex items-center">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-2 text-sm transition-colors',
                activeTab === tab.id
                  ? 'text-vibe-text border-b-2 border-vibe-accent -mb-px'
                  : 'text-vibe-text-secondary hover:text-vibe-text'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
        {/* Both the chevron and the X close the panel (reopen from the preview
            toolbar's "Show panel"). The X was previously a dead no-op. */}
        <div className="flex items-center gap-1 pr-2">
          <button
            data-testid="info-collapse-button"
            onClick={() => setInfoCollapsed(true)}
            title="Collapse panel"
            aria-label="Collapse panel"
            className="p-1 hover:bg-vibe-bg-tertiary rounded text-vibe-text-secondary"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            data-testid="info-close-button"
            onClick={() => setInfoCollapsed(true)}
            title="Close panel"
            aria-label="Close panel"
            className="p-1 hover:bg-vibe-bg-tertiary rounded text-vibe-text-secondary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'problems' && <ProblemsTab />}
        {activeTab === 'output' && <OutputTab />}
        {activeTab === 'terminal' && <TerminalTab />}
        {activeTab === 'git' && <GitTab />}
      </div>
    </div>
  );
}
